import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSchedule,
  sendSchedule,
  emptyWeekSchedule,
  WeekSchedule,
} from "../../services/schedule";
import ScheduleEditor from "./ScheduleEditor";
import Button from "./Button";
import Spinner from "./Spinner";

type Props = {
  sensorName: string;
  isOnline?: boolean;
};

type Draft = { schedule: WeekSchedule; active: boolean };

function serialize(d: Draft): string {
  return JSON.stringify(d);
}

export default function SensorScheduleCard({ sensorName, isOnline }: Props) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [baseline, setBaseline] = useState<string>("");
  const [saved, setSaved] = useState(false);

  const {
    data,
    isPending,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["schedule", sensorName],
    queryFn: async () => {
      const result = await getSchedule(sensorName);
      // Null = device replied but holds no recognisable schedule → empty.
      return (
        result ?? { schedule: emptyWeekSchedule(), schedulerActive: false }
      );
    },
    enabled: !!sensorName,
    staleTime: 30_000,
    retry: false,
  });

  // Seed the editable draft when the device's schedule arrives or genuinely
  // changes server-side. A background refetch returning the same data leaves
  // unsaved edits intact (incoming === baseline → no reset).
  useEffect(() => {
    if (!data) return;
    const incoming: Draft = {
      schedule: data.schedule,
      active: data.schedulerActive,
    };
    const incomingStr = serialize(incoming);
    if (incomingStr !== baseline) {
      setBaseline(incomingStr);
      setDraft(incoming);
    }
  }, [data, baseline]);

  const save = useMutation({
    mutationFn: async (d: Draft) =>
      await sendSchedule(sensorName, d.schedule, d.active),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["schedule", sensorName] });
    },
  });

  const dirty = draft != null && serialize(draft) !== baseline;

  const onEditorChange = (next: WeekSchedule, active: boolean) => {
    setSaved(false);
    setDraft({ schedule: next, active });
  };

  const onDiscard = () => {
    if (!baseline) return;
    setDraft(JSON.parse(baseline));
    setSaved(false);
    save.reset();
  };

  return (
    <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Schedule</h2>
        {isFetching && !isPending && (
          <span className="text-ink-3 text-sm">Refreshing…</span>
        )}
      </div>

      {isPending ? (
        <div className="flex flex-col items-center gap-3 py-10 text-ink-3">
          <Spinner size={48} />
          <span className="text-sm">Reading schedule from the device…</span>
        </div>
      ) : isError ? (
        <div className="text-center py-8">
          <p className="text-ink-2">
            {isOnline === false
              ? "The sensor is offline. Its schedule will load once it reconnects."
              : "The device did not respond. It may be offline."}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            className="!text-ink !border-black/15 hover:!bg-black/[0.04] mt-3"
          >
            Try Again
          </Button>
        </div>
      ) : draft ? (
        <>
          <ScheduleEditor
            value={draft.schedule}
            schedulerActive={draft.active}
            onChange={onEditorChange}
            disabled={save.isPending}
          />

          <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-black/10">
            {save.isPending && (
              <span className="text-ink-3 text-sm">
                Sending to device…
              </span>
            )}
            {save.isError && !save.isPending && (
              <span className="text-alert-critical text-sm">
                Couldn't reach the device. Try again.
              </span>
            )}
            {saved && !dirty && !save.isPending && (
              <span className="text-alert-success text-sm">Saved.</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onDiscard}
              disabled={!dirty || save.isPending}
              className="!text-ink !border-black/15 hover:!bg-black/[0.04] disabled:!opacity-40"
            >
              Discard
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => draft && save.mutate(draft)}
              disabled={!dirty || save.isPending}
            >
              Save Schedule
            </Button>
          </div>
          <p className="text-ink-3 text-xs mt-3">
            Times are in this browser's local time zone. The device must be
            online to apply changes.
          </p>
        </>
      ) : null}
    </div>
  );
}
